import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { customer_register, messageClear } from '../store/reducers/authReducer';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LoaderIcon } from "lucide-react";
import { Eye, EyeOff } from "lucide-react";
import Footer from '../components/Footer';

const Register = () => {
  const { loader, successMessage, errorMessage, userInfo } = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [state, setState] = useState({
    name: '',
    email: '',
    password: '',
    referralCode: ''
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const ref = params.get('ref')
    if (ref) setState(prev => ({ ...prev, referralCode: ref }))
  }, [location.search]);
  const [errors, setErrors] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  const validateName = (name) => {
    if (!name) return "Name is required";
    if (name.length < 2) return "Name must be at least 2 characters";
    if (name.length > 50) return "Name must be less than 50 characters";
    if (!/^[a-zA-Z\s]*$/.test(name)) return "Name can only contain letters and spaces";
    return "";
  };

  const validateEmail = (email) => {
    if (!email) return "Email is required";
    if (email.length < 5) return "Email must be at least 5 characters";
    if (email.length > 100) return "Email must be less than 100 characters";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return "Please enter a valid email address";
    return "";
  };

  const validatePassword = (password) => {
    if (!password) return "Password is required";
    if (password.length < 8) return "Password must be at least 8 characters";
    if (password.length > 100) return "Password must be less than 100 characters";
    if (!/[A-Z]/.test(password)) return "Password must contain at least one uppercase letter";
    if (!/[a-z]/.test(password)) return "Password must contain at least one lowercase letter";
    if (!/[0-9]/.test(password)) return "Password must contain at least one number";
    if (!/[^A-Za-z0-9]/.test(password)) return "Password must contain at least one special character";
    return "";
  };

  const inputHandle = (e) => {
    const { name, value } = e.target;
    setState(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    setErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  };

  const validateForm = () => {
    const newErrors = {
      name: validateName(state.name),
      email: validateEmail(state.email),
      password: validatePassword(state.password)
    };

    setErrors(newErrors);

    // Return true if no errors (all error messages are empty strings)
    return Object.values(newErrors).every(error => error === "");
  };

  const register = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      dispatch(customer_register(state));
    } else {
      // Show toast for the first error found
      const firstError = Object.values(errors).find(error => error !== "");
      if (firstError) {
        toast.error(firstError);
      }
    }
  };

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(messageClear());
    }
    if (errorMessage) {
      toast.error(errorMessage);
      dispatch(messageClear());
    }
    if (userInfo) {
      navigate('/');
    }
    document.documentElement.classList.add('register-page');

    return () => {
      document.documentElement.classList.remove('register-page');
    };
  }, [successMessage, errorMessage, userInfo, dispatch, navigate]);

  return (
    <>
    <div className="p-4 flex flex-col items-start max-w-sm mx-auto h-auto overflow-hidden pt-20 mb-20">
      {loader && (
        <div className="w-screen h-screen flex justify-center items-center fixed left-0 top-0 bg-background/80 z-50">
          <LoaderIcon className="w-5 h-5 animate-spin" />
        </div>
      )}
      
      <div className="flex items-center w-full py-8 border-b border-border/80">
        <Link to="/" className="flex items-center">
          <img
            src={`${process.env.REACT_APP_URL}/images/omahlogoupdated.png`}
            alt="Omah Global"
            className="h-8 w-auto object-contain"
          />
        </Link>
      </div>

      <div className="flex flex-col items-start gap-y-6 py-8 w-full px-0.5">
        <h2 className="text-2xl font-semibold">
          Create your Omah Global account
        </h2>
        <form onSubmit={register} className="w-full">
          <div className="space-y-2 w-full mb-4">
            <Label htmlFor="name">
              Name
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              value={state.name}
              disabled={loader}
              onChange={inputHandle}
              onBlur={() => setErrors(prev => ({ ...prev, name: validateName(state.name) }))}
              placeholder="Enter your name"
              className={`w-full focus-visible:border-foreground ${
                errors.name ? 'border-red-500' : ''
              }`}
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          <div className="space-y-2 w-full mb-4">
            <Label htmlFor="email">
              Email
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={state.email}
              disabled={loader}
              onChange={inputHandle}
              onBlur={() => setErrors(prev => ({ ...prev, email: validateEmail(state.email) }))}
              placeholder="Enter your email"
              className={`w-full focus-visible:border-foreground ${
                errors.email ? 'border-red-500' : ''
              }`}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">
              Password
            </Label>
            <div className="relative w-full">
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={state.password}
                disabled={loader}
                onChange={inputHandle}
                onBlur={() => setErrors(prev => ({ ...prev, password: validatePassword(state.password) }))}
                placeholder="Create a password"
                className={`w-full focus-visible:border-foreground ${
                  errors.password ? 'border-red-500' : ''
                }`}
              />
              <Button
                type="button"
                size="icon"
                variant="ghost"
                disabled={loader}
                className="absolute top-1 right-1 rounded-md"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? 
                  <EyeOff className="w-4 h-4" /> : 
                  <Eye className="w-4 h-4" />
                }
              </Button>
            </div>
            {errors.password && (
              <p className="text-sm text-red-500">{errors.password}</p>
            )}
          </div>

          <div className="space-y-2 w-full mt-4">
            <Label htmlFor="referralCode">
              Referral Code <span className="text-muted-foreground text-xs">(optional)</span>
            </Label>
            <Input
              id="referralCode"
              name="referralCode"
              type="text"
              value={state.referralCode}
              disabled={loader}
              onChange={inputHandle}
              placeholder="Enter referral code if you have one"
              className="w-full focus-visible:border-foreground uppercase"
            />
          </div>

          <div className="mt-4 w-full">
            <Button
              type="submit"
              disabled={loader}
              className="w-full"
            >
              {loader ? (
                <LoaderIcon className="w-5 h-5 animate-spin" />
              ) : "Create account"}
            </Button>
          </div>
        </form>
      </div>

      <div className="flex flex-col items-start w-full mt-4">
        <p className="text-sm text-muted-foreground">
          By creating an account, you agree to our{" "}
          <Link to="/terms" className="text-primary">
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link to="/privacy" className="text-primary">
            Privacy Policy
          </Link>
        </p>
      </div>

      <div className="flex items-start mt-10 border-t border-border/80 py-6 w-full">
        <p className="text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" className="text-primary">
            Sign in
          </Link>
        </p>
      </div>
    </div>
    <Footer />
    </>
  );
};

export default Register;